import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ArtistLayout from "@/components/layout/ArtistLayout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check, Calendar, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ArtistSubmitStep1 = () => {
  const { id } = useParams(); // If editing existing submission
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    artist_name: user?.name || "",
    city: "",
    venue: "",
    date: "",
    s_user_id: ""
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(!!id);

  useEffect(() => {
    if (id) {
      // Fetch existing submission for editing
      const fetchSubmission = async () => {
        try {
          const response = await axios.get(`${API}/artist/submissions/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const sub = response.data;
          setFormData({
            artist_name: sub.artist_name,
            city: sub.city,
            venue: sub.venue,
            date: sub.date,
            s_user_id: sub.s_user_id
          });
        } catch (error) {
          console.error("Failed to fetch submission:", error);
          toast.error("Failed to load submission");
          navigate("/artist");
        } finally {
          setFetchLoading(false);
        }
      };
      fetchSubmission();
    }
  }, [id, token, navigate]);

  // Calculate submission deadline (event date + 3 days)
  const submissionDeadline = useMemo(() => {
    if (!formData.date) return null;
    try {
      const eventDate = new Date(formData.date);
      const deadline = new Date(eventDate);
      deadline.setDate(deadline.getDate() + 3);
      return deadline.toISOString().split('T')[0];
    } catch {
      return null;
    }
  }, [formData.date]);

  // Get today's date for max date restriction
  const today = useMemo(() => {
    return new Date().toISOString().split('T')[0];
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let submissionId = id;
      
      if (id) {
        // Update existing
        await axios.put(`${API}/artist/submissions/${id}/step1`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Details updated!");
      } else {
        // Create new
        const response = await axios.post(`${API}/artist/submissions`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        submissionId = response.data.id;
        toast.success("Details saved! Now upload your media.");
      }
      
      navigate(`/artist/submit/${submissionId}/media`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save details");
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <ArtistLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400">Loading...</p>
        </div>
      </ArtistLayout>
    );
  }

  return (
    <ArtistLayout>
      <div className="max-w-3xl mx-auto animate-fade-in" data-testid="artist-submit-step1">
        {/* Stepper */}
        <div className="wizard-stepper mb-8">
          <div className="wizard-step active">
            <div className="wizard-step-number">1</div>
            <span className="wizard-step-label hidden sm:inline">Artist Details</span>
          </div>
          <div className="w-12 h-0.5 bg-gray-700 mx-2" />
          <div className="wizard-step inactive">
            <div className="wizard-step-number">2</div>
            <span className="wizard-step-label hidden sm:inline">Media Upload</span>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-gray-900 border border-gray-800 p-8">
          <h1 className="text-3xl font-heading text-white mb-2">ARTIST DETAILS</h1>
          <p className="text-gray-400 mb-8">Fill in your campaign information</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="artist_name" className="text-gray-300 uppercase text-xs tracking-wider">
                Name
              </Label>
              <Input
                id="artist_name"
                name="artist_name"
                value={formData.artist_name}
                onChange={handleChange}
                placeholder="Your name"
                className="h-14 bg-black border-gray-700 text-white placeholder:text-gray-600 focus:border-[#00B4E5]"
                required
                data-testid="input-artist-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city" className="text-gray-300 uppercase text-xs tracking-wider">
                City
              </Label>
              <Input
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Event city"
                className="h-14 bg-black border-gray-700 text-white placeholder:text-gray-600 focus:border-[#00B4E5]"
                required
                data-testid="input-city"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="venue" className="text-gray-300 uppercase text-xs tracking-wider">
                Venue
              </Label>
              <Input
                id="venue"
                name="venue"
                value={formData.venue}
                onChange={handleChange}
                placeholder="Venue name"
                className="h-14 bg-black border-gray-700 text-white placeholder:text-gray-600 focus:border-[#00B4E5]"
                required
                data-testid="input-venue"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="date" className="text-gray-300 uppercase text-xs tracking-wider">
                Event Date
              </Label>
              <Input
                id="date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                max={today}
                className="h-14 bg-black border-gray-700 text-white placeholder:text-gray-600 focus:border-[#00B4E5]"
                required
                data-testid="input-date"
              />
              <p className="text-xs text-gray-500">Only past or today's date allowed</p>
              {/* Show submission deadline when date is selected */}
              {submissionDeadline && (
                <div className="mt-3 p-3 bg-[#00B4E5]/20 border border-[#00B4E5]/40 rounded" data-testid="deadline-info">
                  <div className="flex items-center gap-2 text-[#00B4E5]">
                    <Calendar size={16} />
                    <span className="text-sm font-medium">Last Date for Submission</span>
                  </div>
                  <p className="text-white text-lg font-semibold mt-1">{submissionDeadline}</p>
                  <p className="text-gray-400 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} />
                    Submit within 3 days of the event date
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="s_user_id" className="text-gray-300 uppercase text-xs tracking-wider">
                S-User ID
              </Label>
              <Input
                id="s_user_id"
                name="s_user_id"
                value={formData.s_user_id}
                onChange={handleChange}
                placeholder="Your S-User ID"
                className="h-14 bg-black border-gray-700 text-white placeholder:text-gray-600 focus:border-[#00B4E5]"
                required
                data-testid="input-s-user-id"
              />
            </div>

            <div className="pt-6">
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 kf-btn-primary flex items-center justify-center gap-2"
                data-testid="submit-step1-button"
              >
                {loading ? (
                  "Saving..."
                ) : (
                  <>
                    Continue to Media Upload
                    <ArrowRight size={20} />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </ArtistLayout>
  );
};

export default ArtistSubmitStep1;
